import { useEffect, useRef, useState } from "react";
import { api } from "../../api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";

interface AccountAddFormProps {
	onAddAccount: (params: {
		name: string;
		mode:
			| "claude-oauth"
			| "console"
			| "zai"
			| "minimax"
			| "deepseek"
			| "anthropic-compatible"
			| "openai-compatible"
			| "nanogpt"
			| "vertex-ai"
			| "bedrock"
			| "kilo"
			| "openrouter"
			| "alibaba-coding-plan"
			| "codex"
			| "codebuddy"
			| "qwen"
			| "ollama";
		priority: number;
		customEndpoint?: string;
	}) => Promise<{ authUrl: string; sessionId: string }>;
	onCompleteAccount: (params: {
		sessionId: string;
		code: string;
	}) => Promise<void>;
	onAddZaiAccount: (params: {
		name: string;
		apiKey: string;
		priority: number;
		customEndpoint?: string;
		modelMappings?: { [key: string]: string };
	}) => Promise<void>;
	onAddMinimaxAccount: (params: {
		name: string;
		apiKey: string;
		priority: number;
	}) => Promise<void>;
	onAddDeepseekAccount: (params: {
		name: string;
		apiKey: string;
		priority: number;
		modelMappings?: { [key: string]: string };
	}) => Promise<void>;
	onAddAnthropicCompatibleAccount: (params: {
		name: string;
		apiKey: string;
		priority: number;
		customEndpoint?: string;
		modelMappings?: { [key: string]: string };
	}) => Promise<void>;
	onAddNanoGPTAccount: (params: {
		name: string;
		apiKey: string;
		priority: number;
		customEndpoint?: string;
		modelMappings?: { [key: string]: string };
	}) => Promise<void>;
	onAddOpenAIAccount: (params: {
		name: string;
		apiKey: string;
		priority: number;
		customEndpoint: string;
		modelMappings?: { [key: string]: string };
	}) => Promise<void>;
	onAddVertexAIAccount: (params: {
		name: string;
		projectId: string;
		region: string;
		priority: number;
	}) => Promise<void>;
	onAddBedrockAccount: (params: {
		name: string;
		profile: string;
		region: string;
		priority: number;
		cross_region_mode?: "geographic" | "global" | "regional";
		customModel?: string;
	}) => Promise<void>;
	onAddAlibabaCodingPlanAccount: (params: {
		name: string;
		apiKey: string;
		priority: number;
		modelMappings?: { [key: string]: string };
	}) => Promise<void>;
	onAddKiloAccount: (params: {
		name: string;
		apiKey: string;
		priority: number;
		modelMappings?: { [key: string]: string };
	}) => Promise<void>;
	onAddOpenRouterAccount: (params: {
		name: string;
		apiKey: string;
		priority: number;
		modelMappings?: { [key: string]: string };
	}) => Promise<void>;
	onAddOllamaAccount: (params: {
		name: string;
		priority: number;
		customEndpoint?: string;
		modelMappings?: { [key: string]: string };
	}) => Promise<void>;
	onAddOllamaCloudAccount: (params: {
		name: string;
		apiKey: string;
		priority: number;
		modelMappings?: { [key: string]: string };
	}) => Promise<void>;
	onAddMetaAccount: (params: {
		name: string;
		apiKey: string;
		priority: number;
		customEndpoint?: string;
		modelMappings?: { [key: string]: string };
	}) => Promise<void>;
	onCancel: () => void;
	onSuccess: () => void;
	onError: (error: string) => void;
}

export function AccountAddForm({
	onAddAccount,
	onCompleteAccount,
	onAddZaiAccount,
	onAddMinimaxAccount,
	onAddDeepseekAccount,
	onAddAnthropicCompatibleAccount,
	onAddNanoGPTAccount,
	onAddOpenAIAccount,
	onAddVertexAIAccount,
	onAddBedrockAccount,
	onAddAlibabaCodingPlanAccount,
	onAddKiloAccount,
	onAddOpenRouterAccount,
	onAddOllamaAccount,
	onAddOllamaCloudAccount,
	onAddMetaAccount,
	onCancel,
	onSuccess,
	onError,
}: AccountAddFormProps) {
	const [authStep, setAuthStep] = useState<"form" | "code">("form");
	const [authCode, setAuthCode] = useState("");
	const [sessionId, setSessionId] = useState("");
	const [newAccount, setNewAccount] = useState({
		name: "",
		mode: "claude-oauth" as
			| "claude-oauth"
			| "console"
			| "zai"
			| "minimax"
			| "deepseek"
			| "anthropic-compatible"
			| "openai-compatible"
			| "nanogpt"
			| "vertex-ai"
			| "bedrock"
			| "kilo"
			| "openrouter"
			| "alibaba-coding-plan"
			| "codex"
			| "codebuddy"
			| "qwen"
			| "ollama"
			| "ollama-cloud"
			| "meta",
		priority: 0,
		apiKey: "",
		customEndpoint: "",
		projectId: "",
		region: "global",
		profile: "",
		awsRegion: "",
		crossRegionMode: "geographic" as "geographic" | "global" | "regional",
		customBedrockModel: "",
		opusModel: "",
		sonnetModel: "",
		haikuModel: "",
		fableModel: "",
	});

	// Qwen device flow state
	const [qwenStep, setQwenStep] = useState<
		"idle" | "pending" | "complete" | "error"
	>("idle");
	const [qwenAuthUrl, setQwenAuthUrl] = useState("");
	const [qwenUserCode, setQwenUserCode] = useState("");
	const [qwenError, setQwenError] = useState("");
	const qwenSessionIdRef = useRef<string>("");
	const qwenPollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
		null,
	);

	// Codex device flow state
	const [codexStep, setCodexStep] = useState<
		"idle" | "pending" | "complete" | "error"
	>("idle");
	const [codexVerificationUrl, setCodexVerificationUrl] = useState("");
	const [codexUserCode, setCodexUserCode] = useState("");
	const [codexError, setCodexError] = useState("");
	const codexSessionIdRef = useRef<string>("");
	const codexPollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
		null,
	);

	// CodeBuddy browser auth state
	const [codebuddyStep, setCodeBuddyStep] = useState<
		"idle" | "pending" | "complete" | "error"
	>("idle");
	const [codebuddyAuthUrl, setCodeBuddyAuthUrl] = useState("");
	const [codebuddyError, setCodeBuddyError] = useState("");
	const codebuddySessionIdRef = useRef<string>("");
	const codebuddyPollIntervalRef = useRef<ReturnType<
		typeof setInterval
	> | null>(null);
	// Bumped on unmount so a late init result cannot open a tab or start
	// polling after the form is gone.
	const codebuddyAuthAttemptRef = useRef(0);

	const [awsProfiles, setAwsProfiles] = useState<
		Array<{ name: string; region: string | null }>
	>([]);
	const [loadingProfiles, setLoadingProfiles] = useState(false);

	// openai-compatible "Fetch models" state
	const [modelPreviewState, setModelPreviewState] = useState<
		"idle" | "loading" | "success" | "error"
	>("idle");
	const [previewModels, setPreviewModels] = useState<
		Array<{ id: string; displayName: string }>
	>([]);
	const [previewError, setPreviewError] = useState("");
	// The apiKey/endpoint the current (in-flight or last-settled) preview
	// request was started for, so a later edit to either field can invalidate
	// it — including while it's still loading — instead of letting a stale
	// response apply model ids that were never validated against the
	// endpoint the user is now pointing at.
	const previewSourceRef = useRef<{ apiKey: string; endpoint: string } | null>(
		null,
	);
	const previewRequestIdRef = useRef(0);

	// Cleanup Qwen polling on unmount
	useEffect(() => {
		return () => {
			if (qwenPollIntervalRef.current !== null) {
				clearInterval(qwenPollIntervalRef.current);
			}
		};
	}, []);

	// Cleanup Codex polling on unmount
	useEffect(() => {
		return () => {
			if (codexPollIntervalRef.current !== null) {
				clearInterval(codexPollIntervalRef.current);
			}
		};
	}, []);

	// Cleanup CodeBuddy polling on unmount
	useEffect(() => {
		return () => {
			codebuddyAuthAttemptRef.current += 1;
			if (codebuddyPollIntervalRef.current !== null) {
				clearInterval(codebuddyPollIntervalRef.current);
			}
		};
	}, []);

	// Load AWS profiles when bedrock mode is selected
	useEffect(() => {
		if (newAccount.mode === "bedrock") {
			setLoadingProfiles(true);
			api
				.getAwsProfiles()
				.then((profiles) => {
					setAwsProfiles(profiles);
				})
				.catch((error) => {
					console.error("Failed to load AWS profiles:", error);
					setAwsProfiles([]);
				})
				.finally(() => {
					setLoadingProfiles(false);
				});
		}
	}, [newAccount.mode]);

	// Invalidate a fetched model preview once its source apiKey/endpoint no
	// longer matches what's in the form — otherwise editing either field
	// after a successful fetch would silently submit model ids from a
	// different endpoint than the one being saved.
	useEffect(() => {
		const source = previewSourceRef.current;
		if (!source) return;
		if (
			source.apiKey !== newAccount.apiKey ||
			source.endpoint !== newAccount.customEndpoint
		) {
			previewSourceRef.current = null;
			previewRequestIdRef.current += 1;
			setModelPreviewState("idle");
			setPreviewModels([]);
			setPreviewError("");
		}
	}, [newAccount.apiKey, newAccount.customEndpoint]);

	const validateCustomEndpoint = (endpoint: string): boolean => {
		if (!endpoint) return true; // Empty is fine (use default)
		try {
			new URL(endpoint);
			return true;
		} catch {
			return false;
		}
	};

	/**
	 * Mirrors `deriveFamilyDefaults` in
	 * packages/proxy/src/openai-compatible-model-catalog.ts: family defaults by
	 * list position, since a preview fetch has no priority signal beyond the
	 * order the endpoint returned models in.
	 */
	const deriveFamilyDefaults = (
		models: Array<{ id: string; displayName: string }>,
	): { opus: string; sonnet: string; haiku: string } => {
		const at = (index: number) => models[Math.min(index, models.length - 1)].id;
		return { opus: at(0), sonnet: at(1), haiku: at(2) };
	};

	const handleFetchOpenAICompatibleModels = async () => {
		const requestId = ++previewRequestIdRef.current;
		const { apiKey, customEndpoint: endpoint } = newAccount;
		// Recorded at request start, not just on success — so an edit made
		// while this request is still loading is visible to the invalidation
		// effect below immediately, not only after the response comes back.
		previewSourceRef.current = { apiKey, endpoint };
		setModelPreviewState("loading");
		setPreviewError("");
		try {
			const result = await api.previewOpenAICompatibleModels({
				apiKey,
				endpoint,
			});
			// A newer fetch (or an edit that invalidated this one) landed first —
			// applying this response now would overwrite it with stale results.
			if (requestId !== previewRequestIdRef.current) return;
			if (result.models.length === 0) {
				throw new Error("No models returned by this endpoint");
			}
			setPreviewModels(result.models);
			const defaults = deriveFamilyDefaults(result.models);
			setNewAccount((prev) => ({
				...prev,
				opusModel: defaults.opus,
				sonnetModel: defaults.sonnet,
				haikuModel: defaults.haiku,
			}));
			setModelPreviewState("success");
		} catch (err) {
			if (requestId !== previewRequestIdRef.current) return;
			setModelPreviewState("error");
			setPreviewError(
				err instanceof Error ? err.message : "Failed to fetch models",
			);
		}
	};

	const stopQwenPolling = () => {
		if (qwenPollIntervalRef.current !== null) {
			clearInterval(qwenPollIntervalRef.current);
			qwenPollIntervalRef.current = null;
		}
	};

	const stopCodexPolling = () => {
		if (codexPollIntervalRef.current !== null) {
			clearInterval(codexPollIntervalRef.current);
			codexPollIntervalRef.current = null;
		}
	};

	const stopCodeBuddyPolling = () => {
		if (codebuddyPollIntervalRef.current !== null) {
			clearInterval(codebuddyPollIntervalRef.current);
			codebuddyPollIntervalRef.current = null;
		}
	};

	const handleStartCodeBuddyAuth = async () => {
		if (!newAccount.name) {
			onError("Account name is required");
			return;
		}
		setCodeBuddyStep("pending");
		setCodeBuddyError("");
		const attempt = ++codebuddyAuthAttemptRef.current;
		try {
			const result = await api.initCodeBuddyAuth({
				name: newAccount.name,
				priority: newAccount.priority,
			});
			if (codebuddyAuthAttemptRef.current !== attempt) return;
			codebuddySessionIdRef.current = result.sessionId;
			setCodeBuddyAuthUrl(result.authUrl);

			if (typeof window !== "undefined") {
				window.open(result.authUrl, "_blank");
			}

			codebuddyPollIntervalRef.current = setInterval(async () => {
				try {
					const status = await api.getCodeBuddyAuthStatus(
						codebuddySessionIdRef.current,
					);
					if (codebuddyAuthAttemptRef.current !== attempt) return;
					if (status.status === "complete") {
						stopCodeBuddyPolling();
						setCodeBuddyStep("complete");
						setTimeout(() => {
							if (codebuddyAuthAttemptRef.current !== attempt) return;
							setCodeBuddyStep("idle");
							setCodeBuddyAuthUrl("");
							setNewAccount({
								name: "",
								mode: "claude-oauth",
								priority: 0,
								apiKey: "",
								customEndpoint: "",
								projectId: "",
								region: "global",
								profile: "",
								awsRegion: "",
								crossRegionMode: "geographic",
								customBedrockModel: "",
								opusModel: "",
								sonnetModel: "",
								haikuModel: "",
								fableModel: "",
							});
							onSuccess();
						}, 1500);
					} else if (status.status === "error") {
						stopCodeBuddyPolling();
						setCodeBuddyStep("error");
						setCodeBuddyError(status.error || "Authentication failed");
					}
				} catch {
					// Network error — keep polling.
				}
			}, 3000);
		} catch (err) {
			if (codebuddyAuthAttemptRef.current !== attempt) return;
			setCodeBuddyStep("error");
			setCodeBuddyError(
				err instanceof Error ? err.message : "Failed to start authentication",
			);
		}
	};

	const handleStartQwenAuth = async () => {
		if (!newAccount.name) {
			onError("Account name is required");
			return;
		}
		setQwenStep("pending");
		setQwenError("");
		try {
			const result = await api.initQwenDeviceFlow({
				name: newAccount.name,
				priority: newAccount.priority,
			});
			qwenSessionIdRef.current = result.sessionId;
			setQwenAuthUrl(result.authUrl);
			setQwenUserCode(result.userCode);

			// Open auth URL in new tab
			if (typeof window !== "undefined") {
				window.open(result.authUrl, "_blank");
			}

			// Poll for status every 3s
			qwenPollIntervalRef.current = setInterval(async () => {
				try {
					const status = await api.getQwenAuthStatus(qwenSessionIdRef.current);
					if (status.status === "complete") {
						stopQwenPolling();
						setQwenStep("complete");
						setTimeout(() => {
							setQwenStep("idle");
							setQwenAuthUrl("");
							setQwenUserCode("");
							setNewAccount({
								name: "",
								mode: "claude-oauth",
								priority: 0,
								apiKey: "",
								customEndpoint: "",
								projectId: "",
								region: "global",
								profile: "",
								awsRegion: "",
								crossRegionMode: "geographic",
								customBedrockModel: "",
								opusModel: "",
								sonnetModel: "",
								haikuModel: "",
								fableModel: "",
							});
							onSuccess();
						}, 1500);
					} else if (status.status === "error") {
						stopQwenPolling();
						setQwenStep("error");
						setQwenError(status.error || "Authentication failed");
					}
				} catch {
					// Network error — keep polling
				}
			}, 3000);
		} catch (err) {
			setQwenStep("error");
			setQwenError(
				err instanceof Error ? err.message : "Failed to start authentication",
			);
		}
	};

	const handleStartCodexAuth = async () => {
		if (!newAccount.name) {
			onError("Account name is required");
			return;
		}
		setCodexStep("pending");
		setCodexError("");
		try {
			const result = await api.initCodexDeviceFlow({
				name: newAccount.name,
				priority: newAccount.priority,
			});
			codexSessionIdRef.current = result.sessionId;
			setCodexVerificationUrl(result.verificationUrl);
			setCodexUserCode(result.userCode);

			// Open auth URL in new tab
			if (typeof window !== "undefined") {
				window.open(result.verificationUrl, "_blank");
			}

			// Poll for status every 3s
			codexPollIntervalRef.current = setInterval(async () => {
				try {
					const status = await api.getCodexAuthStatus(
						codexSessionIdRef.current,
					);
					if (status.status === "complete") {
						stopCodexPolling();
						setCodexStep("complete");
						setTimeout(() => {
							setCodexStep("idle");
							setCodexVerificationUrl("");
							setCodexUserCode("");
							setNewAccount({
								name: "",
								mode: "claude-oauth",
								priority: 0,
								apiKey: "",
								customEndpoint: "",
								projectId: "",
								region: "global",
								profile: "",
								awsRegion: "",
								crossRegionMode: "geographic",
								customBedrockModel: "",
								opusModel: "",
								sonnetModel: "",
								haikuModel: "",
								fableModel: "",
							});
							onSuccess();
						}, 1500);
					} else if (status.status === "error") {
						stopCodexPolling();
						setCodexStep("error");
						setCodexError(status.error || "Authentication failed");
					}
				} catch {
					// Network error — keep polling
				}
			}, 3000);
		} catch (err) {
			setCodexStep("error");
			setCodexError(
				err instanceof Error ? err.message : "Failed to start authentication",
			);
		}
	};

	const handleAddAccount = async () => {
		if (!newAccount.name) {
			onError("Account name is required");
			return;
		}

		// Validate custom endpoint if provided
		if (
			newAccount.customEndpoint &&
			!validateCustomEndpoint(newAccount.customEndpoint)
		) {
			onError(
				"Custom endpoint must be a valid URL (e.g., https://api.anthropic.com)",
			);
			return;
		}

		const accountParams = {
			name: newAccount.name,
			mode: newAccount.mode as
				| "claude-oauth"
				| "console"
				| "zai"
				| "minimax"
				| "anthropic-compatible"
				| "openai-compatible"
				| "bedrock"
				| "kilo"
				| "openrouter"
				| "alibaba-coding-plan",
			priority: newAccount.priority,
			...(newAccount.customEndpoint && {
				customEndpoint: newAccount.customEndpoint.trim(),
			}),
		};

		if (newAccount.mode === "vertex-ai") {
			if (!newAccount.projectId) {
				onError("Google Cloud Project ID is required for Vertex AI accounts");
				return;
			}
			// For Vertex AI accounts, we don't need OAuth flow
			await onAddVertexAIAccount({
				name: newAccount.name,
				projectId: newAccount.projectId.trim(),
				region: newAccount.region || "global",
				priority: newAccount.priority,
			});
			// Reset form and signal success
			setNewAccount({
				name: "",
				mode: "claude-oauth",
				priority: 0,
				apiKey: "",
				customEndpoint: "",
				projectId: "",
				region: "global",
				profile: "",
				awsRegion: "",
				crossRegionMode: "geographic",
				customBedrockModel: "",
				opusModel: "",
				sonnetModel: "",
				haikuModel: "",
				fableModel: "",
			});
			onSuccess();
			return;
		}

		if (newAccount.mode === "bedrock") {
			if (!newAccount.profile) {
				onError("AWS profile is required for Bedrock accounts");
				return;
			}
			if (!newAccount.awsRegion) {
				onError(
					"Region not found for selected profile. Configure ~/.aws/config",
				);
				return;
			}
			// For Bedrock accounts, we don't need OAuth flow
			await onAddBedrockAccount({
				name: newAccount.name,
				profile: newAccount.profile,
				region: newAccount.awsRegion,
				priority: newAccount.priority,
				cross_region_mode: newAccount.crossRegionMode,
				customModel: newAccount.customBedrockModel || undefined,
			});
			// Reset form and signal success
			setNewAccount({
				name: "",
				mode: "claude-oauth",
				priority: 0,
				apiKey: "",
				customEndpoint: "",
				projectId: "",
				region: "global",
				profile: "",
				awsRegion: "",
				crossRegionMode: "geographic",
				customBedrockModel: "",
				opusModel: "",
				sonnetModel: "",
				haikuModel: "",
				fableModel: "",
			});
			onSuccess();
			return;
		}

		if (newAccount.mode === "zai") {
			if (!newAccount.apiKey) {
				onError("API key is required for z.ai accounts");
				return;
			}
			// Build model mappings from form fields
			const zaiModelMappings: { [key: string]: string } = {};
			if (newAccount.opusModel) zaiModelMappings.opus = newAccount.opusModel;
			if (newAccount.sonnetModel)
				zaiModelMappings.sonnet = newAccount.sonnetModel;
			if (newAccount.haikuModel) zaiModelMappings.haiku = newAccount.haikuModel;
			// For z.ai accounts, we don't need OAuth flow
			await onAddZaiAccount({
				...accountParams,
				apiKey: newAccount.apiKey,
				...(newAccount.customEndpoint && {
					customEndpoint: newAccount.customEndpoint.trim(),
				}),
				...(Object.keys(zaiModelMappings).length > 0 && {
					modelMappings: zaiModelMappings,
				}),
			});
			// Reset form and signal success
			setNewAccount({
				name: "",
				mode: "claude-oauth",
				priority: 0,
				apiKey: "",
				customEndpoint: "",
				projectId: "",
				region: "global",
				profile: "",
				awsRegion: "",
				crossRegionMode: "geographic",
				customBedrockModel: "",
				opusModel: "",
				sonnetModel: "",
				haikuModel: "",
				fableModel: "",
			});
			onSuccess();
			return;
		}

		if (newAccount.mode === "minimax") {
			if (!newAccount.apiKey) {
				onError("API key is required for Minimax accounts");
				return;
			}
			// For Minimax accounts, we don't need OAuth flow and use default tier
			await onAddMinimaxAccount({
				name: newAccount.name,
				apiKey: newAccount.apiKey,
				priority: newAccount.priority,
			});
			// Reset form and signal success
			setNewAccount({
				name: "",
				mode: "claude-oauth",
				priority: 0,
				apiKey: "",
				customEndpoint: "",
				projectId: "",
				region: "global",
				profile: "",
				awsRegion: "",
				crossRegionMode: "geographic",
				customBedrockModel: "",
				opusModel: "",
				sonnetModel: "",
				haikuModel: "",
				fableModel: "",
			});
			onSuccess();
			return;
		}

		if (newAccount.mode === "deepseek") {
			if (!newAccount.apiKey) {
				onError("API key is required for DeepSeek accounts");
				return;
			}
			// Build model mappings from form fields
			const deepseekModelMappings: { [key: string]: string } = {};
			if (newAccount.fableModel) {
				deepseekModelMappings.fable = newAccount.fableModel;
			}
			if (newAccount.opusModel) {
				deepseekModelMappings.opus = newAccount.opusModel;
			}
			if (newAccount.sonnetModel) {
				deepseekModelMappings.sonnet = newAccount.sonnetModel;
			}
			if (newAccount.haikuModel) {
				deepseekModelMappings.haiku = newAccount.haikuModel;
			}
			// For DeepSeek accounts, we don't need OAuth flow and use default tier
			await onAddDeepseekAccount({
				name: newAccount.name,
				apiKey: newAccount.apiKey,
				priority: newAccount.priority,
				modelMappings:
					Object.keys(deepseekModelMappings).length > 0
						? deepseekModelMappings
						: undefined,
			});
			// Reset form and signal success
			setNewAccount({
				name: "",
				mode: "claude-oauth",
				priority: 0,
				apiKey: "",
				customEndpoint: "",
				projectId: "",
				region: "global",
				profile: "",
				awsRegion: "",
				crossRegionMode: "geographic",
				customBedrockModel: "",
				opusModel: "",
				sonnetModel: "",
				haikuModel: "",
				fableModel: "",
			});
			onSuccess();
			return;
		}

		if (newAccount.mode === "nanogpt") {
			if (!newAccount.apiKey) {
				onError("API key is required for NanoGPT accounts");
				return;
			}
			// Build model mappings from form fields
			const modelMappings: { [key: string]: string } = {};
			if (newAccount.opusModel) {
				modelMappings.opus = newAccount.opusModel;
			}
			if (newAccount.sonnetModel) {
				modelMappings.sonnet = newAccount.sonnetModel;
			}
			if (newAccount.haikuModel) {
				modelMappings.haiku = newAccount.haikuModel;
			}
			// For NanoGPT accounts, we don't need OAuth flow
			await onAddNanoGPTAccount({
				name: newAccount.name,
				apiKey: newAccount.apiKey,
				priority: newAccount.priority,
				customEndpoint: newAccount.customEndpoint || undefined,
				modelMappings:
					Object.keys(modelMappings).length > 0 ? modelMappings : undefined,
			});
			// Reset form and signal success
			setNewAccount({
				name: "",
				mode: "claude-oauth",
				priority: 0,
				apiKey: "",
				customEndpoint: "",
				projectId: "",
				region: "global",
				profile: "",
				awsRegion: "",
				crossRegionMode: "geographic",
				customBedrockModel: "",
				opusModel: "",
				sonnetModel: "",
				haikuModel: "",
				fableModel: "",
			});
			onSuccess();
			return;
		}

		if (newAccount.mode === "kilo") {
			if (!newAccount.apiKey) {
				onError("API key is required for Kilo Gateway accounts");
				return;
			}
			const kiloModelMappings: { [key: string]: string } = {};
			if (newAccount.opusModel) kiloModelMappings.opus = newAccount.opusModel;
			if (newAccount.sonnetModel)
				kiloModelMappings.sonnet = newAccount.sonnetModel;
			if (newAccount.haikuModel)
				kiloModelMappings.haiku = newAccount.haikuModel;
			await onAddKiloAccount({
				name: newAccount.name,
				apiKey: newAccount.apiKey,
				priority: newAccount.priority,
				modelMappings:
					Object.keys(kiloModelMappings).length > 0
						? kiloModelMappings
						: undefined,
			});
			setNewAccount({
				name: "",
				mode: "claude-oauth",
				priority: 0,
				apiKey: "",
				customEndpoint: "",
				projectId: "",
				region: "global",
				profile: "",
				awsRegion: "",
				crossRegionMode: "geographic",
				customBedrockModel: "",
				opusModel: "",
				sonnetModel: "",
				haikuModel: "",
				fableModel: "",
			});
			onSuccess();
			return;
		}

		if (newAccount.mode === "alibaba-coding-plan") {
			if (!newAccount.apiKey) {
				onError("API key is required for Alibaba Coding Plan accounts");
				return;
			}
			const modelMappings: { [key: string]: string } = {};
			if (newAccount.opusModel) modelMappings.opus = newAccount.opusModel;
			if (newAccount.sonnetModel) modelMappings.sonnet = newAccount.sonnetModel;
			if (newAccount.haikuModel) modelMappings.haiku = newAccount.haikuModel;
			await onAddAlibabaCodingPlanAccount({
				name: newAccount.name,
				apiKey: newAccount.apiKey,
				priority: newAccount.priority,
				modelMappings:
					Object.keys(modelMappings).length > 0 ? modelMappings : undefined,
			});
			setNewAccount({
				name: "",
				mode: "claude-oauth",
				priority: 0,
				apiKey: "",
				customEndpoint: "",
				projectId: "",
				region: "global",
				profile: "",
				awsRegion: "",
				crossRegionMode: "geographic",
				customBedrockModel: "",
				opusModel: "",
				sonnetModel: "",
				haikuModel: "",
				fableModel: "",
			});
			onSuccess();
			return;
		}

		if (newAccount.mode === "openrouter") {
			if (!newAccount.apiKey) {
				onError("API key is required for OpenRouter accounts");
				return;
			}
			const modelMappings: { [key: string]: string } = {};
			if (newAccount.opusModel) modelMappings.opus = newAccount.opusModel;
			if (newAccount.sonnetModel) modelMappings.sonnet = newAccount.sonnetModel;
			if (newAccount.haikuModel) modelMappings.haiku = newAccount.haikuModel;
			await onAddOpenRouterAccount({
				name: newAccount.name,
				apiKey: newAccount.apiKey,
				priority: newAccount.priority,
				modelMappings:
					Object.keys(modelMappings).length > 0 ? modelMappings : undefined,
			});
			setNewAccount({
				name: "",
				mode: "claude-oauth",
				priority: 0,
				apiKey: "",
				customEndpoint: "",
				projectId: "",
				region: "global",
				profile: "",
				awsRegion: "",
				crossRegionMode: "geographic",
				customBedrockModel: "",
				opusModel: "",
				sonnetModel: "",
				haikuModel: "",
				fableModel: "",
			});
			onSuccess();
			return;
		}

		if (newAccount.mode === "anthropic-compatible") {
			if (!newAccount.apiKey) {
				onError("API key is required for Anthropic-compatible accounts");
				return;
			}
			// Build model mappings object
			const modelMappings: { [key: string]: string } = {};
			if (newAccount.opusModel) modelMappings.opus = newAccount.opusModel;
			if (newAccount.sonnetModel) modelMappings.sonnet = newAccount.sonnetModel;
			if (newAccount.haikuModel) modelMappings.haiku = newAccount.haikuModel;

			// For Anthropic-compatible accounts, we don't need OAuth flow and use default tier
			await onAddAnthropicCompatibleAccount({
				name: newAccount.name,
				apiKey: newAccount.apiKey,
				priority: newAccount.priority,
				customEndpoint: newAccount.customEndpoint || undefined,
				modelMappings:
					Object.keys(modelMappings).length > 0 ? modelMappings : undefined,
			});
			// Reset form and signal success
			setNewAccount({
				name: "",
				mode: "claude-oauth",
				priority: 0,
				apiKey: "",
				customEndpoint: "",
				projectId: "",
				region: "global",
				profile: "",
				awsRegion: "",
				crossRegionMode: "geographic",
				customBedrockModel: "",
				opusModel: "",
				sonnetModel: "",
				haikuModel: "",
				fableModel: "",
			});
			onSuccess();
			return;
		}

		if (newAccount.mode === "openai-compatible") {
			if (!newAccount.apiKey) {
				onError("API key is required for OpenAI-compatible accounts");
				return;
			}
			if (!newAccount.customEndpoint) {
				onError("Endpoint URL is required for OpenAI-compatible accounts");
				return;
			}

			// Build model mappings object
			const modelMappings: { [key: string]: string } = {};
			if (newAccount.opusModel) modelMappings.opus = newAccount.opusModel;
			if (newAccount.sonnetModel) modelMappings.sonnet = newAccount.sonnetModel;
			if (newAccount.haikuModel) modelMappings.haiku = newAccount.haikuModel;

			// For OpenAI-compatible accounts, we don't need OAuth flow
			await onAddOpenAIAccount({
				name: newAccount.name,
				apiKey: newAccount.apiKey,
				priority: newAccount.priority,
				customEndpoint: newAccount.customEndpoint.trim(),
				modelMappings:
					Object.keys(modelMappings).length > 0 ? modelMappings : undefined,
			});

			// Reset form and signal success
			setNewAccount({
				name: "",
				mode: "claude-oauth",
				priority: 0,
				apiKey: "",
				customEndpoint: "",
				projectId: "",
				region: "global",
				profile: "",
				awsRegion: "",
				crossRegionMode: "geographic",
				customBedrockModel: "",
				opusModel: "",
				sonnetModel: "",
				haikuModel: "",
				fableModel: "",
			});
			previewSourceRef.current = null;
			previewRequestIdRef.current += 1;
			setModelPreviewState("idle");
			setPreviewModels([]);
			setPreviewError("");
			onSuccess();
			return;
		}

		if (newAccount.mode === "ollama") {
			const modelMappings: { [key: string]: string } = {};
			if (newAccount.opusModel) modelMappings.opus = newAccount.opusModel;
			if (newAccount.sonnetModel) modelMappings.sonnet = newAccount.sonnetModel;
			if (newAccount.haikuModel) modelMappings.haiku = newAccount.haikuModel;

			await onAddOllamaAccount({
				name: newAccount.name,
				priority: newAccount.priority,
				customEndpoint: newAccount.customEndpoint || undefined,
				modelMappings:
					Object.keys(modelMappings).length > 0 ? modelMappings : undefined,
			});
			setNewAccount({
				name: "",
				mode: "claude-oauth",
				priority: 0,
				apiKey: "",
				customEndpoint: "",
				projectId: "",
				region: "global",
				profile: "",
				awsRegion: "",
				crossRegionMode: "geographic",
				customBedrockModel: "",
				opusModel: "",
				sonnetModel: "",
				haikuModel: "",
				fableModel: "",
			});
			onSuccess();
			return;
		}

		if (newAccount.mode === "ollama-cloud") {
			if (!newAccount.apiKey) {
				onError("API key is required for Ollama Cloud");
				return;
			}
			const modelMappings: { [key: string]: string } = {};
			if (newAccount.opusModel) modelMappings.opus = newAccount.opusModel;
			if (newAccount.sonnetModel) modelMappings.sonnet = newAccount.sonnetModel;
			if (newAccount.haikuModel) modelMappings.haiku = newAccount.haikuModel;

			await onAddOllamaCloudAccount({
				name: newAccount.name,
				apiKey: newAccount.apiKey,
				priority: newAccount.priority,
				modelMappings:
					Object.keys(modelMappings).length > 0 ? modelMappings : undefined,
			});
			setNewAccount({
				name: "",
				mode: "claude-oauth",
				priority: 0,
				apiKey: "",
				customEndpoint: "",
				projectId: "",
				region: "global",
				profile: "",
				awsRegion: "",
				crossRegionMode: "geographic",
				customBedrockModel: "",
				opusModel: "",
				sonnetModel: "",
				haikuModel: "",
				fableModel: "",
			});
			onSuccess();
			return;
		}

		if (newAccount.mode === "meta") {
			if (!newAccount.apiKey) {
				onError("API key is required for Meta Model API");
				return;
			}
			const modelMappings: { [key: string]: string } = {};
			if (newAccount.opusModel) modelMappings.opus = newAccount.opusModel;
			if (newAccount.sonnetModel) modelMappings.sonnet = newAccount.sonnetModel;
			if (newAccount.haikuModel) modelMappings.haiku = newAccount.haikuModel;

			await onAddMetaAccount({
				name: newAccount.name,
				apiKey: newAccount.apiKey,
				priority: newAccount.priority,
				customEndpoint: newAccount.customEndpoint || undefined,
				modelMappings:
					Object.keys(modelMappings).length > 0 ? modelMappings : undefined,
			});
			setNewAccount({
				name: "",
				mode: "claude-oauth",
				priority: 0,
				apiKey: "",
				customEndpoint: "",
				projectId: "",
				region: "global",
				profile: "",
				awsRegion: "",
				crossRegionMode: "geographic",
				customBedrockModel: "",
				opusModel: "",
				sonnetModel: "",
				haikuModel: "",
				fableModel: "",
			});
			onSuccess();
			return;
		}

		// Step 1: Initialize OAuth flow for Max/Console accounts
		const { authUrl, sessionId } = await onAddAccount(accountParams);
		setSessionId(sessionId);

		// Open auth URL in new tab
		if (typeof window !== "undefined") {
			window.open(authUrl, "_blank");
		}

		// Move to code entry step
		setAuthStep("code");
	};

	const handleCodeSubmit = async () => {
		const trimmedCode = authCode.trim();
		if (!trimmedCode) {
			onError("Authorization code is required");
			return;
		}
		// Step 2: Complete OAuth flow
		await onCompleteAccount({
			sessionId,
			code: trimmedCode,
		});

		// Success! Reset form
		setAuthStep("form");
		setAuthCode("");
		setSessionId("");
		setNewAccount({
			name: "",
			mode: "claude-oauth",
			priority: 0,
			apiKey: "",
			customEndpoint: "",
			projectId: "",
			region: "global",
			profile: "",
			awsRegion: "",
			crossRegionMode: "geographic",
			customBedrockModel: "",
			opusModel: "",
			sonnetModel: "",
			haikuModel: "",
			fableModel: "",
		});
		onSuccess();
	};

	const handleCancel = () => {
		stopQwenPolling();
		setQwenStep("idle");
		setQwenAuthUrl("");
		setQwenUserCode("");
		setQwenError("");
		stopCodexPolling();
		setCodexStep("idle");
		setCodexVerificationUrl("");
		setCodexUserCode("");
		setCodexError("");
		setAuthStep("form");
		setAuthCode("");
		setSessionId("");
		setNewAccount({
			name: "",
			mode: "claude-oauth",
			priority: 0,
			apiKey: "",
			customEndpoint: "",
			projectId: "",
			region: "global",
			profile: "",
			awsRegion: "",
			crossRegionMode: "geographic",
			customBedrockModel: "",
			opusModel: "",
			sonnetModel: "",
			haikuModel: "",
			fableModel: "",
		});
		previewSourceRef.current = null;
		previewRequestIdRef.current += 1;
		setModelPreviewState("idle");
		setPreviewModels([]);
		setPreviewError("");
		onCancel();
	};

	return (
		<div className="space-y-4 mb-6 p-4 border rounded-lg">
			<h4 className="font-medium">
				{authStep === "form" ? "Add New Account" : "Enter Authorization Code"}
			</h4>
			{authStep === "form" && (
				<>
					<div className="space-y-2">
						<Label htmlFor="name">Account Name</Label>
						<Input
							id="name"
							value={newAccount.name}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
								setNewAccount({
									...newAccount,
									name: (e.target as HTMLInputElement).value,
								})
							}
							placeholder="e.g., work-account or user@example.com"
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="mode">Mode</Label>
						<Select
							value={newAccount.mode}
							onValueChange={(
								value:
									| "claude-oauth"
									| "console"
									| "zai"
									| "minimax"
									| "deepseek"
									| "anthropic-compatible"
									| "openai-compatible"
									| "bedrock"
									| "kilo"
									| "openrouter"
									| "codex"
									| "qwen"
									| "ollama"
									| "ollama-cloud"
									| "meta",
							) => setNewAccount({ ...newAccount, mode: value })}
						>
							<SelectTrigger id="mode">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="claude-oauth">
									Claude CLI OAuth (Recommended)
								</SelectItem>
								<SelectItem value="console">Claude API</SelectItem>
								<SelectItem value="codex">Codex (OpenAI OAuth)</SelectItem>
								<SelectItem value="codebuddy">CodeBuddy (Tencent)</SelectItem>
								<SelectItem value="qwen">Qwen (Alibaba Cloud OAuth)</SelectItem>
								<SelectItem value="vertex-ai">
									Vertex AI (Google Cloud)
								</SelectItem>
								<SelectItem value="bedrock">AWS Bedrock</SelectItem>
								<SelectItem value="zai">z.ai (API Key)</SelectItem>
								<SelectItem value="minimax">Minimax (API Key)</SelectItem>
								<SelectItem value="deepseek">DeepSeek (API Key)</SelectItem>
								<SelectItem value="nanogpt">NanoGPT (API Key)</SelectItem>
								<SelectItem value="anthropic-compatible">
									Anthropic-Compatible (API Key)
								</SelectItem>
								<SelectItem value="openai-compatible">
									OpenAI-Compatible (API Key)
								</SelectItem>
								<SelectItem value="kilo">Kilo Gateway (API Key)</SelectItem>
								<SelectItem value="openrouter">OpenRouter (API Key)</SelectItem>
								<SelectItem value="alibaba-coding-plan">
									Alibaba Coding Plan International (API Key)
								</SelectItem>
								<SelectItem value="ollama">Ollama (v0.14.0+, local)</SelectItem>
								<SelectItem value="ollama-cloud">
									Ollama Cloud (ollama.com)
								</SelectItem>
								<SelectItem value="meta">Meta Model API (API Key)</SelectItem>
							</SelectContent>
						</Select>
					</div>
					{newAccount.mode === "codex" && (
						<div className="space-y-3">
							{codexStep === "idle" && (
								<div className="bg-blue-50 dark:bg-blue-950 p-3 rounded-lg">
									<p className="text-sm text-blue-900 dark:text-blue-100 font-medium mb-1">
										Device Code Authentication
									</p>
									<p className="text-xs text-blue-800 dark:text-blue-200">
										Click the button below to start Codex authentication. A
										browser tab will open for you to authorize.
									</p>
								</div>
							)}
							{codexStep === "pending" && (
								<div className="bg-blue-50 dark:bg-blue-950 p-3 rounded-lg space-y-2">
									<p className="text-sm text-blue-900 dark:text-blue-100 font-medium">
										Waiting for authorization...
									</p>
									<p className="text-xs text-blue-800 dark:text-blue-200">
										Enter this code in the browser tab:
									</p>
									<div className="flex items-center gap-2">
										<code className="text-lg font-mono font-bold tracking-widest bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100 px-3 py-1 rounded">
											{codexUserCode}
										</code>
										<a
											href={codexVerificationUrl}
											target="_blank"
											rel="noreferrer"
											className="text-xs text-blue-700 dark:text-blue-300 underline"
										>
											Open browser
										</a>
									</div>
								</div>
							)}
							{codexStep === "complete" && (
								<div className="bg-green-50 dark:bg-green-950 p-3 rounded-lg">
									<p className="text-sm text-green-900 dark:text-green-100 font-medium">
										Authorization successful! Account added.
									</p>
								</div>
							)}
							{codexStep === "error" && (
								<div className="bg-red-50 dark:bg-red-950 p-3 rounded-lg space-y-2">
									<p className="text-sm text-red-900 dark:text-red-100 font-medium">
										Authentication failed
									</p>
									<p className="text-xs text-red-800 dark:text-red-200">
										{codexError}
									</p>
									<Button
										variant="outline"
										size="sm"
										onClick={() => {
											setCodexStep("idle");
											setCodexError("");
										}}
									>
										Try again
									</Button>
								</div>
							)}
						</div>
					)}
					{newAccount.mode === "codebuddy" && (
						<div className="space-y-3">
							{codebuddyStep === "idle" && (
								<div className="bg-blue-50 dark:bg-blue-950 p-3 rounded-lg">
									<p className="text-sm text-blue-900 dark:text-blue-100 font-medium mb-1">
										Browser Authentication
									</p>
									<p className="text-xs text-blue-800 dark:text-blue-200">
										Click the button below to start CodeBuddy authentication. A
										browser tab will open for you to sign in.
									</p>
								</div>
							)}
							{codebuddyStep === "pending" && (
								<div className="bg-blue-50 dark:bg-blue-950 p-3 rounded-lg space-y-2">
									<p className="text-sm text-blue-900 dark:text-blue-100 font-medium">
										Waiting for authorization...
									</p>
									<p className="text-xs text-blue-800 dark:text-blue-200">
										Complete the sign-in in the browser tab.
									</p>
									<a
										href={codebuddyAuthUrl}
										target="_blank"
										rel="noreferrer"
										className="text-xs text-blue-700 dark:text-blue-300 underline break-all"
									>
										{codebuddyAuthUrl}
									</a>
								</div>
							)}
							{codebuddyStep === "complete" && (
								<div className="bg-green-50 dark:bg-green-950 p-3 rounded-lg">
									<p className="text-sm text-green-900 dark:text-green-100 font-medium">
										Authorization successful! Account added.
									</p>
								</div>
							)}
							{codebuddyStep === "error" && (
								<div className="bg-red-50 dark:bg-red-950 p-3 rounded-lg space-y-2">
									<p className="text-sm text-red-900 dark:text-red-100 font-medium">
										Authentication failed
									</p>
									<p className="text-xs text-red-800 dark:text-red-200">
										{codebuddyError}
									</p>
									<Button
										variant="outline"
										size="sm"
										onClick={() => {
											setCodeBuddyStep("idle");
											setCodeBuddyError("");
										}}
									>
										Try again
									</Button>
								</div>
							)}
						</div>
					)}
					{newAccount.mode === "qwen" && (
						<div className="space-y-3">
							{qwenStep === "idle" && (
								<div className="bg-blue-50 dark:bg-blue-950 p-3 rounded-lg">
									<p className="text-sm text-blue-900 dark:text-blue-100 font-medium mb-1">
										Device Code Authentication
									</p>
									<p className="text-xs text-blue-800 dark:text-blue-200">
										Click the button below to start Qwen authentication. A
										browser tab will open for you to authorize.
									</p>
								</div>
							)}
							{qwenStep === "pending" && (
								<div className="bg-blue-50 dark:bg-blue-950 p-3 rounded-lg space-y-2">
									<p className="text-sm text-blue-900 dark:text-blue-100 font-medium">
										Waiting for authorization...
									</p>
									<p className="text-xs text-blue-800 dark:text-blue-200">
										Enter this code in the browser tab:
									</p>
									<div className="flex items-center gap-2">
										<code className="text-lg font-mono font-bold tracking-widest bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100 px-3 py-1 rounded">
											{qwenUserCode}
										</code>
										<a
											href={qwenAuthUrl}
											target="_blank"
											rel="noreferrer"
											className="text-xs text-blue-700 dark:text-blue-300 underline"
										>
											Open browser
										</a>
									</div>
								</div>
							)}
							{qwenStep === "complete" && (
								<div className="bg-green-50 dark:bg-green-950 p-3 rounded-lg">
									<p className="text-sm text-green-900 dark:text-green-100 font-medium">
										Authorization successful! Account added.
									</p>
								</div>
							)}
							{qwenStep === "error" && (
								<div className="bg-red-50 dark:bg-red-950 p-3 rounded-lg space-y-2">
									<p className="text-sm text-red-900 dark:text-red-100 font-medium">
										Authentication failed
									</p>
									<p className="text-xs text-red-800 dark:text-red-200">
										{qwenError}
									</p>
									<Button
										variant="outline"
										size="sm"
										onClick={() => {
											setQwenStep("idle");
											setQwenError("");
										}}
									>
										Try again
									</Button>
								</div>
							)}
						</div>
					)}
					{newAccount.mode === "vertex-ai" && (
						<>
							<div className="space-y-2">
								<Label htmlFor="projectId">Google Cloud Project ID</Label>
								<Input
									id="projectId"
									value={newAccount.projectId}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
										setNewAccount({
											...newAccount,
											projectId: (e.target as HTMLInputElement).value,
										})
									}
									placeholder="your-project-id"
								/>
								<p className="text-xs text-muted-foreground">
									Your Google Cloud project ID where Vertex AI is enabled
								</p>
							</div>
							<div className="space-y-2">
								<Label htmlFor="region">Region</Label>
								<Select
									value={newAccount.region}
									onValueChange={(value: string) =>
										setNewAccount({ ...newAccount, region: value })
									}
								>
									<SelectTrigger id="region">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="global">Global (Recommended)</SelectItem>
										<SelectItem value="us-east5">us-east5</SelectItem>
										<SelectItem value="us-central1">us-central1</SelectItem>
										<SelectItem value="europe-west1">europe-west1</SelectItem>
										<SelectItem value="europe-west4">europe-west4</SelectItem>
										<SelectItem value="asia-southeast1">
											asia-southeast1
										</SelectItem>
									</SelectContent>
								</Select>
								<p className="text-xs text-muted-foreground">
									Global for best availability, regional for data residency
								</p>
							</div>
							<div className="bg-blue-50 dark:bg-blue-950 p-3 rounded-lg">
								<p className="text-sm text-blue-900 dark:text-blue-100 font-medium mb-1">
									Authentication Required
								</p>
								<p className="text-xs text-blue-800 dark:text-blue-200">
									Vertex AI uses Google Cloud credentials. Ensure you've run:{" "}
									<code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">
										gcloud auth application-default login
									</code>
								</p>
							</div>
						</>
					)}
					{newAccount.mode === "bedrock" && (
						<>
							{awsProfiles.length === 0 && !loadingProfiles && (
								<div className="bg-blue-50 dark:bg-blue-950 p-3 rounded-lg">
									<p className="text-sm text-blue-900 dark:text-blue-100 font-medium mb-1">
										No AWS profiles found
									</p>
									<p className="text-xs text-blue-800 dark:text-blue-200">
										Run{" "}
										<code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">
											aws configure
										</code>{" "}
										to set up profiles.
									</p>
								</div>
							)}
							{awsProfiles.length > 0 && (
								<>
									<div className="space-y-2">
										<Label htmlFor="awsProfile">AWS Profile</Label>
										<Select
											value={newAccount.profile}
											onValueChange={(value: string) => {
												const selectedProfile = awsProfiles.find(
													(p) => p.name === value,
												);
												setNewAccount({
													...newAccount,
													profile: value,
													awsRegion: selectedProfile?.region || "",
												});
											}}
										>
											<SelectTrigger id="awsProfile">
												<SelectValue placeholder="Select AWS profile" />
											</SelectTrigger>
											<SelectContent>
												{awsProfiles.map((profile) => (
													<SelectItem key={profile.name} value={profile.name}>
														{profile.name}
														{profile.region && ` (${profile.region})`}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										<p className="text-xs text-muted-foreground">
											Your AWS profile from ~/.aws/credentials
										</p>
									</div>
									<div className="space-y-2">
										<Label htmlFor="awsRegion">Region (Auto-detected)</Label>
										<Input
											id="awsRegion"
											value={newAccount.awsRegion}
											disabled
											placeholder="Select profile to detect region"
										/>
										<p className="text-xs text-muted-foreground">
											Region from ~/.aws/config for selected profile
										</p>
										{newAccount.profile &&
											!newAccount.awsRegion &&
											!loadingProfiles && (
												<p className="text-xs text-yellow-600 dark:text-yellow-400">
													No default region found for this profile. Configure
													region in ~/.aws/config
												</p>
											)}
									</div>
									<div className="space-y-2">
										<Label htmlFor="crossRegionMode">Cross-Region Mode</Label>
										<Select
											value={newAccount.crossRegionMode}
											onValueChange={(
												value: "geographic" | "global" | "regional",
											) =>
												setNewAccount({ ...newAccount, crossRegionMode: value })
											}
										>
											<SelectTrigger id="crossRegionMode">
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="geographic">
													Geographic (default - routes within your region's
													geography)
												</SelectItem>
												<SelectItem value="global">
													Global (routes globally, ~10% cost savings, premium
													models only)
												</SelectItem>
												<SelectItem value="regional">
													Regional (single region, no failover)
												</SelectItem>
											</SelectContent>
										</Select>
										<p className="text-xs text-muted-foreground">
											Controls how Bedrock routes requests for cross-region
											inference
										</p>
									</div>
									<div className="space-y-2">
										<Label htmlFor="customBedrockModel">
											Custom Model ID (Optional)
										</Label>
										<Input
											id="customBedrockModel"
											value={newAccount.customBedrockModel}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
												setNewAccount({
													...newAccount,
													customBedrockModel: (e.target as HTMLInputElement)
														.value,
												})
											}
											placeholder="e.g., anthropic.claude-opus-4-6-v1:0"
										/>
										<p className="text-xs text-muted-foreground">
											Specify a Bedrock model ID to bypass automatic model
											detection. Leave empty to use fuzzy matching.
										</p>
									</div>
									<div className="bg-blue-50 dark:bg-blue-950 p-3 rounded-lg">
										<p className="text-sm text-blue-900 dark:text-blue-100 font-medium mb-1">
											Authentication Required
										</p>
										<p className="text-xs text-blue-800 dark:text-blue-200">
											Bedrock uses AWS credentials from the selected profile.
											Ensure your credentials are configured.
										</p>
									</div>
								</>
							)}
						</>
					)}
					{newAccount.mode === "zai" && (
						<>
							<div className="space-y-2">
								<Label htmlFor="apiKey">z.ai API Key</Label>
								<Input
									id="apiKey"
									type="password"
									value={newAccount.apiKey}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
										setNewAccount({
											...newAccount,
											apiKey: (e.target as HTMLInputElement).value,
										})
									}
									placeholder="Enter your z.ai API key"
								/>
							</div>
							<div className="space-y-2">
								<Label className="text-sm font-medium">
									Model Mappings (Optional)
								</Label>
								<p className="text-xs text-muted-foreground">
									Map Anthropic model names to z.ai-specific models. Leave empty
									to use Claude models directly.
								</p>
								<div className="space-y-2 pl-4">
									<div>
										<Label htmlFor="opusModel" className="text-sm">
											Opus Model
										</Label>
										<Input
											id="opusModel"
											value={newAccount.opusModel}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
												setNewAccount({
													...newAccount,
													opusModel: (e.target as HTMLInputElement).value,
												})
											}
											placeholder="e.g. glm-4.5-flash"
											className="mt-1"
										/>
									</div>
									<div>
										<Label htmlFor="sonnetModel" className="text-sm">
											Sonnet Model
										</Label>
										<Input
											id="sonnetModel"
											value={newAccount.sonnetModel}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
												setNewAccount({
													...newAccount,
													sonnetModel: (e.target as HTMLInputElement).value,
												})
											}
											placeholder="e.g. glm-4.5-flash"
											className="mt-1"
										/>
									</div>
									<div>
										<Label htmlFor="haikuModel" className="text-sm">
											Haiku Model
										</Label>
										<Input
											id="haikuModel"
											value={newAccount.haikuModel}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
												setNewAccount({
													...newAccount,
													haikuModel: (e.target as HTMLInputElement).value,
												})
											}
											placeholder="e.g. glm-4.5-air"
											className="mt-1"
										/>
									</div>
								</div>
							</div>
						</>
					)}
					{newAccount.mode === "minimax" && (
						<div className="space-y-2">
							<Label htmlFor="apiKey">Minimax API Key</Label>
							<Input
								id="apiKey"
								type="password"
								value={newAccount.apiKey}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
									setNewAccount({
										...newAccount,
										apiKey: (e.target as HTMLInputElement).value,
									})
								}
								placeholder="Enter your Minimax API key"
							/>
						</div>
					)}
					{newAccount.mode === "deepseek" && (
						<>
							<div className="space-y-2">
								<Label htmlFor="apiKey">DeepSeek API Key</Label>
								<Input
									id="apiKey"
									type="password"
									value={newAccount.apiKey}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
										setNewAccount({
											...newAccount,
											apiKey: (e.target as HTMLInputElement).value,
										})
									}
									placeholder="Enter your DeepSeek API key"
								/>
							</div>
							<div className="space-y-2">
								<Label className="text-sm font-medium">
									Model Mappings (Optional)
								</Label>
								<p className="text-xs text-muted-foreground">
									Map Anthropic model names to DeepSeek-specific models. Leave
									empty to use DeepSeek's automatic mapping.
								</p>
								<div className="space-y-2 pl-4">
									<div>
										<Label htmlFor="deepseekFableModel" className="text-sm">
											Fable Model
										</Label>
										<Input
											id="deepseekFableModel"
											value={newAccount.fableModel}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
												setNewAccount({
													...newAccount,
													fableModel: (e.target as HTMLInputElement).value,
												})
											}
											placeholder="Custom fable model"
											className="mt-1"
										/>
									</div>
									<div>
										<Label htmlFor="deepseekOpusModel" className="text-sm">
											Opus Model
										</Label>
										<Input
											id="deepseekOpusModel"
											value={newAccount.opusModel}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
												setNewAccount({
													...newAccount,
													opusModel: (e.target as HTMLInputElement).value,
												})
											}
											placeholder="Custom opus model"
											className="mt-1"
										/>
									</div>
									<div>
										<Label htmlFor="deepseekSonnetModel" className="text-sm">
											Sonnet Model
										</Label>
										<Input
											id="deepseekSonnetModel"
											value={newAccount.sonnetModel}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
												setNewAccount({
													...newAccount,
													sonnetModel: (e.target as HTMLInputElement).value,
												})
											}
											placeholder="Custom sonnet model"
											className="mt-1"
										/>
									</div>
									<div>
										<Label htmlFor="deepseekHaikuModel" className="text-sm">
											Haiku Model
										</Label>
										<Input
											id="deepseekHaikuModel"
											value={newAccount.haikuModel}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
												setNewAccount({
													...newAccount,
													haikuModel: (e.target as HTMLInputElement).value,
												})
											}
											placeholder="Custom haiku model"
											className="mt-1"
										/>
									</div>
								</div>
							</div>
						</>
					)}
					{newAccount.mode === "nanogpt" && (
						<>
							<div className="space-y-2">
								<Label htmlFor="apiKey">NanoGPT API Key</Label>
								<Input
									id="apiKey"
									type="password"
									value={newAccount.apiKey}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
										setNewAccount({
											...newAccount,
											apiKey: (e.target as HTMLInputElement).value,
										})
									}
									placeholder="Enter your NanoGPT API key"
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="customEndpoint">
									Custom Endpoint (Optional)
								</Label>
								<Input
									id="customEndpoint"
									type="url"
									value={newAccount.customEndpoint}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
										setNewAccount({
											...newAccount,
											customEndpoint: (e.target as HTMLInputElement).value,
										})
									}
									placeholder="https://nano-gpt.com/api (default)"
								/>
							</div>
							<div className="space-y-2">
								<Label className="text-sm font-medium">
									Model Mappings (Optional)
								</Label>
								<p className="text-xs text-muted-foreground">
									Map Anthropic model names to NanoGPT-specific models. Leave
									empty to use defaults.
								</p>
								<div className="space-y-2 pl-4">
									<div>
										<Label htmlFor="opusModel" className="text-sm">
											Opus Model
										</Label>
										<Input
											id="opusModel"
											value={newAccount.opusModel}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
												setNewAccount({
													...newAccount,
													opusModel: (e.target as HTMLInputElement).value,
												})
											}
											placeholder="nanogpt-ultra (default)"
											className="mt-1"
										/>
									</div>
									<div>
										<Label htmlFor="sonnetModel" className="text-sm">
											Sonnet Model
										</Label>
										<Input
											id="sonnetModel"
											value={newAccount.sonnetModel}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
												setNewAccount({
													...newAccount,
													sonnetModel: (e.target as HTMLInputElement).value,
												})
											}
											placeholder="nanogpt-pro (default)"
											className="mt-1"
										/>
									</div>
									<div>
										<Label htmlFor="haikuModel" className="text-sm">
											Haiku Model
										</Label>
										<Input
											id="haikuModel"
											value={newAccount.haikuModel}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
												setNewAccount({
													...newAccount,
													haikuModel: (e.target as HTMLInputElement).value,
												})
											}
											placeholder="nanogpt-lite (default)"
											className="mt-1"
										/>
									</div>
								</div>
							</div>
						</>
					)}
					{newAccount.mode === "kilo" && (
						<>
							<div className="space-y-2">
								<Label htmlFor="apiKey">Kilo API Key</Label>
								<Input
									id="apiKey"
									type="password"
									value={newAccount.apiKey}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
										setNewAccount({
											...newAccount,
											apiKey: (e.target as HTMLInputElement).value,
										})
									}
									placeholder="Enter your Kilo API key"
								/>
								<p className="text-xs text-muted-foreground">
									Endpoint: https://api.kilo.ai/api/gateway
								</p>
							</div>
							<div className="space-y-2">
								<Label className="text-sm font-medium">
									Model Mappings (Optional)
								</Label>
								<p className="text-xs text-muted-foreground">
									Map Anthropic model names to Kilo-specific models. Leave empty
									to use defaults.
								</p>
								<div className="space-y-2 pl-4">
									<div>
										<Label htmlFor="kiloOpusModel" className="text-sm">
											Opus Model
										</Label>
										<Input
											id="kiloOpusModel"
											value={newAccount.opusModel}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
												setNewAccount({
													...newAccount,
													opusModel: (e.target as HTMLInputElement).value,
												})
											}
											placeholder="e.g., anthropic/claude-opus-4-6 (default)"
											className="mt-1"
										/>
									</div>
									<div>
										<Label htmlFor="kiloSonnetModel" className="text-sm">
											Sonnet Model
										</Label>
										<Input
											id="kiloSonnetModel"
											value={newAccount.sonnetModel}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
												setNewAccount({
													...newAccount,
													sonnetModel: (e.target as HTMLInputElement).value,
												})
											}
											placeholder="e.g., anthropic/claude-sonnet-4-6 (default)"
											className="mt-1"
										/>
									</div>
									<div>
										<Label htmlFor="kiloHaikuModel" className="text-sm">
											Haiku Model
										</Label>
										<Input
											id="kiloHaikuModel"
											value={newAccount.haikuModel}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
												setNewAccount({
													...newAccount,
													haikuModel: (e.target as HTMLInputElement).value,
												})
											}
											placeholder="e.g., anthropic/claude-haiku-4-5 (default)"
											className="mt-1"
										/>
									</div>
								</div>
							</div>
						</>
					)}
					{newAccount.mode === "openrouter" && (
						<>
							<div className="space-y-2">
								<Label htmlFor="apiKey">OpenRouter API Key</Label>
								<Input
									id="apiKey"
									type="password"
									value={newAccount.apiKey}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
										setNewAccount({
											...newAccount,
											apiKey: (e.target as HTMLInputElement).value,
										})
									}
									placeholder="Enter your OpenRouter API key"
								/>
								<p className="text-xs text-muted-foreground">
									Endpoint: https://openrouter.ai/api
								</p>
							</div>
							<div className="space-y-2">
								<Label className="text-sm font-medium">
									Model Mappings (Optional)
								</Label>
								<p className="text-xs text-muted-foreground">
									Map Anthropic model names to OpenRouter-specific models. Leave
									empty to pass model names through unchanged.
								</p>
								<div className="space-y-2 pl-4">
									<div>
										<Label htmlFor="opusModel" className="text-sm">
											Opus Model
										</Label>
										<Input
											id="opusModel"
											value={newAccount.opusModel}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
												setNewAccount({
													...newAccount,
													opusModel: (e.target as HTMLInputElement).value,
												})
											}
											placeholder="e.g., anthropic/claude-opus-4-5"
											className="mt-1"
										/>
									</div>
									<div>
										<Label htmlFor="sonnetModel" className="text-sm">
											Sonnet Model
										</Label>
										<Input
											id="sonnetModel"
											value={newAccount.sonnetModel}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
												setNewAccount({
													...newAccount,
													sonnetModel: (e.target as HTMLInputElement).value,
												})
											}
											placeholder="e.g., anthropic/claude-sonnet-4-5"
											className="mt-1"
										/>
									</div>
									<div>
										<Label htmlFor="haikuModel" className="text-sm">
											Haiku Model
										</Label>
										<Input
											id="haikuModel"
											value={newAccount.haikuModel}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
												setNewAccount({
													...newAccount,
													haikuModel: (e.target as HTMLInputElement).value,
												})
											}
											placeholder="e.g., anthropic/claude-haiku-4-5"
											className="mt-1"
										/>
									</div>
								</div>
							</div>
						</>
					)}
					{newAccount.mode === "alibaba-coding-plan" && (
						<>
							<div className="space-y-2">
								<Label htmlFor="apiKey">Alibaba Coding Plan API Key</Label>
								<Input
									id="apiKey"
									type="password"
									value={newAccount.apiKey}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
										setNewAccount({
											...newAccount,
											apiKey: (e.target as HTMLInputElement).value,
										})
									}
									placeholder="Enter your Alibaba Coding Plan API key"
								/>
								<p className="text-xs text-muted-foreground">
									Endpoint: https://bailian-singapore-cs.alibabacloud.com
								</p>
							</div>
							<div className="space-y-2">
								<Label className="text-sm font-medium">
									Model Mappings (Optional)
								</Label>
								<p className="text-xs text-muted-foreground">
									Map Anthropic model names to Alibaba-specific models. Leave
									empty to use defaults.
								</p>
								<div className="space-y-2 pl-4">
									<div>
										<Label htmlFor="alibabaOpusModel" className="text-sm">
											Opus Model
										</Label>
										<Input
											id="alibabaOpusModel"
											value={newAccount.opusModel}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
												setNewAccount({
													...newAccount,
													opusModel: (e.target as HTMLInputElement).value,
												})
											}
											placeholder="e.g., qwen-max (default)"
											className="mt-1"
										/>
									</div>
									<div>
										<Label htmlFor="alibabaSonnetModel" className="text-sm">
											Sonnet Model
										</Label>
										<Input
											id="alibabaSonnetModel"
											value={newAccount.sonnetModel}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
												setNewAccount({
													...newAccount,
													sonnetModel: (e.target as HTMLInputElement).value,
												})
											}
											placeholder="e.g., qwen-plus (default)"
											className="mt-1"
										/>
									</div>
									<div>
										<Label htmlFor="alibabaHaikuModel" className="text-sm">
											Haiku Model
										</Label>
										<Input
											id="alibabaHaikuModel"
											value={newAccount.haikuModel}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
												setNewAccount({
													...newAccount,
													haikuModel: (e.target as HTMLInputElement).value,
												})
											}
											placeholder="e.g., qwen-turbo (default)"
											className="mt-1"
										/>
									</div>
								</div>
							</div>
						</>
					)}
					{newAccount.mode === "anthropic-compatible" && (
						<>
							<div className="space-y-2">
								<Label htmlFor="apiKey">Anthropic-Compatible API Key</Label>
								<Input
									id="apiKey"
									type="password"
									value={newAccount.apiKey}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
										setNewAccount({
											...newAccount,
											apiKey: (e.target as HTMLInputElement).value,
										})
									}
									placeholder="Enter your Anthropic-Compatible API key"
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="customEndpoint">
									Custom Endpoint URL (Optional)
								</Label>
								<Input
									id="customEndpoint"
									type="url"
									value={newAccount.customEndpoint}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
										setNewAccount({
											...newAccount,
											customEndpoint: (e.target as HTMLInputElement).value,
										})
									}
									placeholder="https://api.anthropic-compatible.com"
								/>
							</div>
							<div className="space-y-2">
								<Label>Model Mappings (Optional)</Label>
								<p className="text-xs text-muted-foreground mb-2">
									Map Anthropic model names to provider-specific models. Leave
									empty to use defaults.
								</p>
								<div className="space-y-2 pl-4">
									<div>
										<Label htmlFor="opusModel" className="text-sm">
											Opus Model
										</Label>
										<Input
											id="opusModel"
											value={newAccount.opusModel}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
												setNewAccount({
													...newAccount,
													opusModel: (e.target as HTMLInputElement).value,
												})
											}
											placeholder="claude-3-opus-20240229 (default)"
											className="mt-1"
										/>
									</div>
									<div>
										<Label htmlFor="sonnetModel" className="text-sm">
											Sonnet Model
										</Label>
										<Input
											id="sonnetModel"
											value={newAccount.sonnetModel}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
												setNewAccount({
													...newAccount,
													sonnetModel: (e.target as HTMLInputElement).value,
												})
											}
											placeholder="claude-3-sonnet-20240229 (default)"
											className="mt-1"
										/>
									</div>
									<div>
										<Label htmlFor="haikuModel" className="text-sm">
											Haiku Model
										</Label>
										<Input
											id="haikuModel"
											value={newAccount.haikuModel}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
												setNewAccount({
													...newAccount,
													haikuModel: (e.target as HTMLInputElement).value,
												})
											}
											placeholder="claude-3-haiku-20240307 (default)"
											className="mt-1"
										/>
									</div>
								</div>
							</div>
						</>
					)}
					{newAccount.mode === "meta" && (
						<>
							<div className="space-y-2">
								<Label htmlFor="apiKey">Meta Model API Key</Label>
								<Input
									id="apiKey"
									type="password"
									value={newAccount.apiKey}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
										setNewAccount({
											...newAccount,
											apiKey: (e.target as HTMLInputElement).value,
										})
									}
									placeholder="Enter your Meta Model API key"
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="customEndpoint">
									Custom Endpoint URL (Optional)
								</Label>
								<Input
									id="customEndpoint"
									type="url"
									value={newAccount.customEndpoint}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
										setNewAccount({
											...newAccount,
											customEndpoint: (e.target as HTMLInputElement).value,
										})
									}
									placeholder="https://api.meta.ai"
								/>
							</div>
							<div className="space-y-2">
								<Label>Model Mappings (Optional)</Label>
								<p className="text-xs text-muted-foreground mb-2">
									Map Anthropic model names to Meta models. Leave empty to use
									defaults.
								</p>
								<div className="space-y-2 pl-4">
									<div>
										<Label htmlFor="opusModel" className="text-sm">
											Opus Model
										</Label>
										<Input
											id="opusModel"
											value={newAccount.opusModel}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
												setNewAccount({
													...newAccount,
													opusModel: (e.target as HTMLInputElement).value,
												})
											}
											placeholder="muse-spark-1.2 (default)"
											className="mt-1"
										/>
									</div>
									<div>
										<Label htmlFor="sonnetModel" className="text-sm">
											Sonnet Model
										</Label>
										<Input
											id="sonnetModel"
											value={newAccount.sonnetModel}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
												setNewAccount({
													...newAccount,
													sonnetModel: (e.target as HTMLInputElement).value,
												})
											}
											placeholder="muse-spark-1.2 (default)"
											className="mt-1"
										/>
									</div>
									<div>
										<Label htmlFor="haikuModel" className="text-sm">
											Haiku Model
										</Label>
										<Input
											id="haikuModel"
											value={newAccount.haikuModel}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
												setNewAccount({
													...newAccount,
													haikuModel: (e.target as HTMLInputElement).value,
												})
											}
											placeholder="muse-spark-1.2 (default)"
											className="mt-1"
										/>
									</div>
								</div>
							</div>
						</>
					)}
					{newAccount.mode === "openai-compatible" && (
						<>
							<div className="space-y-2">
								<Label htmlFor="apiKey">API Key</Label>
								<Input
									id="apiKey"
									type="password"
									value={newAccount.apiKey}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
										setNewAccount({
											...newAccount,
											apiKey: (e.target as HTMLInputElement).value,
										})
									}
									placeholder="Enter your API key"
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="endpoint">Endpoint URL</Label>
								<Input
									id="endpoint"
									value={newAccount.customEndpoint}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
										setNewAccount({
											...newAccount,
											customEndpoint: (e.target as HTMLInputElement).value,
										})
									}
									placeholder="https://openrouter.ai/api"
								/>
								<p className="text-xs text-muted-foreground">
									Enter the base URL for the OpenAI-compatible API
								</p>
							</div>
							<div className="space-y-2">
								<Button
									type="button"
									variant="outline"
									size="sm"
									disabled={
										!newAccount.apiKey ||
										!newAccount.customEndpoint ||
										modelPreviewState === "loading"
									}
									onClick={handleFetchOpenAICompatibleModels}
								>
									{modelPreviewState === "loading"
										? "Fetching…"
										: "Fetch models"}
								</Button>
								{modelPreviewState === "error" && (
									<p className="text-xs text-red-600 dark:text-red-400">
										{previewError} — you can still enter model ids manually
										below.
									</p>
								)}
								{modelPreviewState === "success" && (
									<p className="text-xs text-muted-foreground">
										Found {previewModels.length} model
										{previewModels.length === 1 ? "" : "s"} at this endpoint.
									</p>
								)}
							</div>
							<div className="space-y-2">
								<Label>Model Mappings (Optional)</Label>
								<p className="text-xs text-muted-foreground mb-2">
									Map Anthropic model names to provider-specific models. Leave
									empty to use defaults.
								</p>
								{modelPreviewState === "success" ? (
									<div className="space-y-2 pl-4">
										<div>
											<Label htmlFor="opusModel" className="text-sm">
												Opus Model
											</Label>
											<Select
												value={newAccount.opusModel}
												onValueChange={(value: string) =>
													setNewAccount({ ...newAccount, opusModel: value })
												}
											>
												<SelectTrigger id="opusModel" className="mt-1">
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													{previewModels.map((model) => (
														<SelectItem key={model.id} value={model.id}>
															{model.displayName}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>
										<div>
											<Label htmlFor="sonnetModel" className="text-sm">
												Sonnet Model
											</Label>
											<Select
												value={newAccount.sonnetModel}
												onValueChange={(value: string) =>
													setNewAccount({ ...newAccount, sonnetModel: value })
												}
											>
												<SelectTrigger id="sonnetModel" className="mt-1">
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													{previewModels.map((model) => (
														<SelectItem key={model.id} value={model.id}>
															{model.displayName}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>
										<div>
											<Label htmlFor="haikuModel" className="text-sm">
												Haiku Model
											</Label>
											<Select
												value={newAccount.haikuModel}
												onValueChange={(value: string) =>
													setNewAccount({ ...newAccount, haikuModel: value })
												}
											>
												<SelectTrigger id="haikuModel" className="mt-1">
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													{previewModels.map((model) => (
														<SelectItem key={model.id} value={model.id}>
															{model.displayName}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>
									</div>
								) : (
									<div className="space-y-2 pl-4">
										<div>
											<Label htmlFor="opusModel" className="text-sm">
												Opus Model
											</Label>
											<Input
												id="opusModel"
												value={newAccount.opusModel}
												onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
													setNewAccount({
														...newAccount,
														opusModel: (e.target as HTMLInputElement).value,
													})
												}
												placeholder="openai/gpt-5 (default)"
												className="mt-1"
											/>
										</div>
										<div>
											<Label htmlFor="sonnetModel" className="text-sm">
												Sonnet Model
											</Label>
											<Input
												id="sonnetModel"
												value={newAccount.sonnetModel}
												onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
													setNewAccount({
														...newAccount,
														sonnetModel: (e.target as HTMLInputElement).value,
													})
												}
												placeholder="openai/gpt-5 (default)"
												className="mt-1"
											/>
										</div>
										<div>
											<Label htmlFor="haikuModel" className="text-sm">
												Haiku Model
											</Label>
											<Input
												id="haikuModel"
												value={newAccount.haikuModel}
												onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
													setNewAccount({
														...newAccount,
														haikuModel: (e.target as HTMLInputElement).value,
													})
												}
												placeholder="openai/gpt-5-mini (default)"
												className="mt-1"
											/>
										</div>
									</div>
								)}
							</div>
						</>
					)}
					{newAccount.mode === "ollama" && (
						<>
							<div className="space-y-2">
								<Label htmlFor="customEndpoint">
									Ollama Endpoint URL (Optional)
								</Label>
								<Input
									id="customEndpoint"
									type="url"
									value={newAccount.customEndpoint}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
										setNewAccount({
											...newAccount,
											customEndpoint: (e.target as HTMLInputElement).value,
										})
									}
									placeholder="http://localhost:11434"
								/>
								<p className="text-xs text-muted-foreground">
									Leave empty to use default http://localhost:11434. Requires
									Ollama v0.14.0+.
								</p>
							</div>
							<div className="space-y-2">
								<Label>Model Mappings (Optional)</Label>
								<p className="text-xs text-muted-foreground mb-2">
									Map Anthropic model names to Ollama model names (e.g.
									qwen3-coder, llama3.3).
								</p>
								<div className="space-y-2 pl-4">
									<div>
										<Label htmlFor="opusModel" className="text-sm">
											Opus Model
										</Label>
										<Input
											id="opusModel"
											value={newAccount.opusModel}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
												setNewAccount({
													...newAccount,
													opusModel: (e.target as HTMLInputElement).value,
												})
											}
											placeholder="qwen3-coder (example)"
											className="mt-1"
										/>
									</div>
									<div>
										<Label htmlFor="sonnetModel" className="text-sm">
											Sonnet Model
										</Label>
										<Input
											id="sonnetModel"
											value={newAccount.sonnetModel}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
												setNewAccount({
													...newAccount,
													sonnetModel: (e.target as HTMLInputElement).value,
												})
											}
											placeholder="qwen3-coder (example)"
											className="mt-1"
										/>
									</div>
									<div>
										<Label htmlFor="haikuModel" className="text-sm">
											Haiku Model
										</Label>
										<Input
											id="haikuModel"
											value={newAccount.haikuModel}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
												setNewAccount({
													...newAccount,
													haikuModel: (e.target as HTMLInputElement).value,
												})
											}
											placeholder="llama3.3 (example)"
											className="mt-1"
										/>
									</div>
								</div>
							</div>
						</>
					)}
					{newAccount.mode === "ollama-cloud" && (
						<>
							<div className="space-y-2">
								<Label htmlFor="apiKey">Ollama Cloud API Key</Label>
								<Input
									id="apiKey"
									type="password"
									value={newAccount.apiKey}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
										setNewAccount({
											...newAccount,
											apiKey: (e.target as HTMLInputElement).value,
										})
									}
									placeholder="Enter your Ollama Cloud API key"
								/>
							</div>
							<div className="space-y-2">
								<Label>Model Mappings (Optional)</Label>
								<p className="text-xs text-muted-foreground mb-2">
									Map Anthropic model names to Ollama model names (e.g.
									qwen3-coder, llama3.3).
								</p>
								<div className="space-y-2 pl-4">
									<div>
										<Label htmlFor="opusModel" className="text-sm">
											Opus Model
										</Label>
										<Input
											id="opusModel"
											value={newAccount.opusModel}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
												setNewAccount({
													...newAccount,
													opusModel: (e.target as HTMLInputElement).value,
												})
											}
											placeholder="qwen3-coder (example)"
											className="mt-1"
										/>
									</div>
									<div>
										<Label htmlFor="sonnetModel" className="text-sm">
											Sonnet Model
										</Label>
										<Input
											id="sonnetModel"
											value={newAccount.sonnetModel}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
												setNewAccount({
													...newAccount,
													sonnetModel: (e.target as HTMLInputElement).value,
												})
											}
											placeholder="qwen3-coder (example)"
											className="mt-1"
										/>
									</div>
									<div>
										<Label htmlFor="haikuModel" className="text-sm">
											Haiku Model
										</Label>
										<Input
											id="haikuModel"
											value={newAccount.haikuModel}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
												setNewAccount({
													...newAccount,
													haikuModel: (e.target as HTMLInputElement).value,
												})
											}
											placeholder="llama3.3 (example)"
											className="mt-1"
										/>
									</div>
								</div>
							</div>
						</>
					)}
					{(newAccount.mode === "claude-oauth" ||
						newAccount.mode === "console") && (
						<div className="space-y-2">
							<Label htmlFor="customEndpoint">
								Custom Endpoint URL (Optional)
							</Label>
							<Input
								id="customEndpoint"
								type="url"
								value={newAccount.customEndpoint}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
									setNewAccount({
										...newAccount,
										customEndpoint: (e.target as HTMLInputElement).value,
									})
								}
								placeholder="https://api.anthropic.com"
							/>
							<p className="text-xs text-muted-foreground">
								Leave empty to use default Anthropic endpoint. Must be a valid
								URL.
							</p>
						</div>
					)}
					<div className="space-y-2">
						<Label htmlFor="priority">Priority</Label>
						<Select
							value={String(newAccount.priority)}
							onValueChange={(value: string) =>
								setNewAccount({ ...newAccount, priority: parseInt(value, 10) })
							}
						>
							<SelectTrigger id="priority">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="0">0 (Highest)</SelectItem>
								<SelectItem value="25">25 (High)</SelectItem>
								<SelectItem value="50">50 (Medium)</SelectItem>
								<SelectItem value="75">75 (Low)</SelectItem>
								<SelectItem value="100">100 (Lowest)</SelectItem>
							</SelectContent>
						</Select>
					</div>
				</>
			)}
			{authStep === "form" ? (
				<div className="flex gap-2">
					{newAccount.mode === "qwen" ? (
						<>
							{(qwenStep === "idle" || qwenStep === "error") && (
								<Button onClick={handleStartQwenAuth}>
									Start Qwen Authentication
								</Button>
							)}
							<Button variant="outline" onClick={handleCancel}>
								Cancel
							</Button>
						</>
					) : newAccount.mode === "codex" ? (
						<>
							{(codexStep === "idle" || codexStep === "error") && (
								<Button onClick={handleStartCodexAuth}>
									Start Codex Authentication
								</Button>
							)}
							<Button variant="outline" onClick={handleCancel}>
								Cancel
							</Button>
						</>
					) : newAccount.mode === "codebuddy" ? (
						<>
							{(codebuddyStep === "idle" || codebuddyStep === "error") && (
								<Button onClick={handleStartCodeBuddyAuth}>
									Start CodeBuddy Authentication
								</Button>
							)}
							<Button variant="outline" onClick={handleCancel}>
								Cancel
							</Button>
						</>
					) : (
						<>
							<Button onClick={handleAddAccount}>Continue</Button>
							<Button variant="outline" onClick={handleCancel}>
								Cancel
							</Button>
						</>
					)}
				</div>
			) : (
				<>
					<div className="space-y-2">
						<p className="text-sm text-muted-foreground">
							A new browser tab has opened for authentication. After
							authorizing, copy the code and paste it below.
						</p>
						<Label htmlFor="code">Authorization Code</Label>
						<Input
							id="code"
							value={authCode}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
								setAuthCode((e.target as HTMLInputElement).value)
							}
							placeholder="Paste authorization code here"
						/>
					</div>
					<div className="flex gap-2">
						<Button onClick={handleCodeSubmit}>Complete Setup</Button>
						<Button variant="outline" onClick={handleCancel}>
							Cancel
						</Button>
					</div>
				</>
			)}
		</div>
	);
}
